import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { FileText, Download, CheckCircle2, ScrollText, ShieldCheck } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function MetricRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-b-0" style={{ borderColor: "var(--rule)" }}>
      <span className="text-sm">{label}</span>
      <span className="font-bold text-default" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function InstituteReports() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/institute/report-summary").then((r) => setData(r.data)).catch((e) => setErr(formatError(e)));
  }, []);

  // CSV downloads must hit the backend directly with a token header. We use
  // axios so the auth header is set, then save the blob client-side.
  const download = async (path, filename) => {
    try {
      const resp = await api.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr(formatError(e)); }
  };

  if (err) return <div className="card card-pad text-red-600" data-testid="reports-error">{err}</div>;
  if (!data) return <div className="text-muted" data-testid="reports-loading">Loading report summary…</div>;

  return (
    <div className="space-y-6" data-testid="institute-reports-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="eyebrow flex items-center gap-2"><FileText size={12} /> Accreditation reports</span>
          <h2 className="font-display font-black text-3xl mt-1">NAAC &amp; NBA — auto-computed</h2>
          <p className="text-muted mt-1 text-sm">
            Pre-filled exports for <b>{data.institute}</b> · academic year <b>{data.academic_year}</b>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-primary"
            onClick={() => download("/institute/naac-csv", `naac-5.2-${data.academic_year}.csv`)}
            data-testid="download-naac-csv"
          >
            <Download size={14} /> NAAC 5.2 CSV
          </button>
          <button
            className="btn-secondary"
            onClick={() => download("/institute/nba-csv", `nba-4-${data.academic_year}.csv`)}
            data-testid="download-nba-csv"
          >
            <Download size={14} /> NBA 4 CSV
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card card-pad" data-testid="naac-block">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-blue-50 text-signal"><ScrollText size={16} /></div>
            <div>
              <span className="eyebrow">NAAC Criterion 5.2</span>
              <div className="font-display font-bold">Student Progression</div>
            </div>
          </div>
          <div className="mt-4">
            <MetricRow label="Total students on platform"               value={data.students_total}              testid="naac-students-total" />
            <MetricRow label="Students placed (offer / hired / joined)" value={data.students_placed}             testid="naac-students-placed" />
            <MetricRow label="Placement percentage"                     value={`${data.placement_pct}%`}         testid="naac-placement-pct" />
            <MetricRow label="Students opting for higher studies"       value={data.higher_studies}              testid="naac-higher-studies" />
            <MetricRow label="Students opting for entrepreneurship"     value={data.entrepreneurs}               testid="naac-entrepreneurs" />
            <MetricRow label="Average profile completeness"             value={`${data.avg_profile_completeness}%`} testid="naac-completeness" />
            <MetricRow label="Total job applications submitted"         value={data.applications_total}          testid="naac-applications" />
          </div>
        </div>

        <div className="card card-pad" data-testid="nba-block">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-emerald-50 text-emerald-600"><ShieldCheck size={16} /></div>
            <div>
              <span className="eyebrow">NBA Criterion 4</span>
              <div className="font-display font-bold">Student Performance</div>
            </div>
          </div>
          <div className="mt-4">
            <MetricRow label="Enrolled students"           value={data.students_total}       testid="nba-students-total" />
            <MetricRow label="Placed students"             value={data.students_placed}      testid="nba-students-placed" />
            <MetricRow label="Placement percentage"        value={`${data.placement_pct}%`}  testid="nba-placement-pct" />
            <MetricRow label="Higher studies"              value={data.higher_studies}       testid="nba-higher-studies" />
            <MetricRow label="Entrepreneurship"            value={data.entrepreneurs}        testid="nba-entrepreneurs" />
          </div>
        </div>
      </div>

      <div className="card card-pad bg-blue-50/50" style={{ borderColor: "var(--rule)" }}>
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <b>Tip for assessors:</b> these CSV exports follow the canonical NAAC / NBA self-study templates and can be
            uploaded as supporting evidence. For a deeper audit trail, attach the full activity log from{" "}
            <a href={`${API_BASE}/api/sitemap-urls`} target="_blank" rel="noopener noreferrer" className="underline">platform exports</a>.
          </div>
        </div>
      </div>
    </div>
  );
}
